import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsProps {
  trackingId?: string;
  plausibleDomain?: string;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Check if new visitor
const isNewVisitor = (): boolean => {
  const visited = localStorage.getItem('analytics_visited');
  if (!visited) {
    localStorage.setItem('analytics_visited', 'true');
    return true;
  }
  return false;
};

// Parse UTM parameters
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
  };
};

// Get device info
const getDeviceType = (ua: string): string => {
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};

const getBrowser = (ua: string): string => {
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
};

// Constants for input validation (security)
const MAX_REFERRER_LENGTH = 2000;
const MAX_SESSION_ID_LENGTH = 100;
const MAX_PAGE_PATH_LENGTH = 500;

// Validate and truncate strings for security
const sanitizeString = (value: string | null, maxLength: number): string | null => {
  if (!value) return null;
  return value.slice(0, maxLength);
};

// Validate Google Analytics tracking ID format
const isValidTrackingId = (id: string): boolean => {
  // GA4: G-XXXXXXXXXX or UA: UA-XXXXXX-X
  const GA4_REGEX = /^G-[A-Z0-9]{10,12}$/;
  const UA_REGEX = /^UA-[0-9]+-[0-9]+$/;
  return GA4_REGEX.test(id) || UA_REGEX.test(id);
};

export const Analytics = ({ trackingId, plausibleDomain }: AnalyticsProps) => {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const hasTrackedPageView = useRef<string>('');
  const lastPath = useRef<string>('');

  // Track scroll depth
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
  }, []);

  // Track exit with time on page and scroll depth
  const trackExit = useCallback(async () => {
    if (!lastPath.current) return;
    
    const timeOnPage = Math.round((Date.now() - pageStartTime.current) / 1000);
    const sessionId = sanitizeString(getSessionId(), MAX_SESSION_ID_LENGTH);

    try {
      await supabase.from('analytics_events').insert({
        event_type: 'exit',
        page_path: sanitizeString(lastPath.current, MAX_PAGE_PATH_LENGTH),
        session_id: sessionId,
        time_on_page: timeOnPage,
        scroll_depth: maxScrollDepth.current,
        is_bounce: timeOnPage < 10 && maxScrollDepth.current < 25,
        device_type: getDeviceType(navigator.userAgent),
        browser: getBrowser(navigator.userAgent),
      });
    } catch (error) {
      console.error('Exit tracking error:', error);
    }
  }, []);

  // Track page views
  useEffect(() => {
    const trackPageView = async () => {
      // Don't track same page twice in same render
      if (hasTrackedPageView.current === location.pathname) return;
      hasTrackedPageView.current = location.pathname;

      // Track exit for previous page
      if (lastPath.current && lastPath.current !== location.pathname) {
        await trackExit();
      }

      // Reset metrics for new page
      pageStartTime.current = Date.now();
      maxScrollDepth.current = 0;
      lastPath.current = location.pathname;

      try {
        const userAgent = navigator.userAgent;
        const deviceType = getDeviceType(userAgent);
        const browser = getBrowser(userAgent);
        const sessionId = sanitizeString(getSessionId(), MAX_SESSION_ID_LENGTH);
        const utmParams = getUTMParams();
        const newVisitor = isNewVisitor();

        // Get first page of session
        const entryPage = sessionStorage.getItem('analytics_entry_page');
        if (!entryPage) {
          sessionStorage.setItem('analytics_entry_page', location.pathname);
        }

        // Sanitize all string inputs for security
        // Note: We only store parsed browser/device_type, NOT full user_agent for privacy
        await supabase.from('analytics_events').insert({
          event_type: 'page_view',
          page_path: sanitizeString(location.pathname, MAX_PAGE_PATH_LENGTH),
          referrer: sanitizeString(document.referrer, MAX_REFERRER_LENGTH),
          device_type: deviceType,
          browser: browser,
          session_id: sessionId,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          language: navigator.language?.slice(0, 20) || null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 100) || null,
          utm_source: sanitizeString(utmParams.utm_source, 100),
          utm_medium: sanitizeString(utmParams.utm_medium, 100),
          utm_campaign: sanitizeString(utmParams.utm_campaign, 200),
          entry_page: sanitizeString(entryPage || location.pathname, MAX_PAGE_PATH_LENGTH),
          is_new_visitor: newVisitor,
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
    };

    trackPageView();
  }, [location.pathname, trackExit]);

  // Set up scroll tracking and exit tracking
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', trackExit);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', trackExit);
    };
  }, [handleScroll, trackExit]);

  // External analytics (GA4 / Plausible)
  useEffect(() => {
    if (trackingId) {
      // SECURITY: Validate tracking ID format before injection to prevent XSS
      if (!isValidTrackingId(trackingId)) {
        console.error('Invalid Google Analytics tracking ID format');
        return;
      }

      const script1 = document.createElement('script');
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(trackingId)}`;
      script1.async = true;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      // Use textContent instead of innerHTML for safer script injection
      script2.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${trackingId}');
      `;
      document.head.appendChild(script2);

      return () => {
        document.head.removeChild(script1);
        document.head.removeChild(script2);
      };
    }

    if (plausibleDomain) {
      // SECURITY: Validate plausible domain format
      const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/;
      if (!DOMAIN_REGEX.test(plausibleDomain) || plausibleDomain.length > 253) {
        console.error('Invalid Plausible domain format');
        return;
      }

      const script = document.createElement('script');
      script.defer = true;
      script.setAttribute('data-domain', plausibleDomain);
      script.src = 'https://plausible.io/js/script.js';
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [trackingId, plausibleDomain]);

  return null;
};

export default Analytics;